import { apiClient } from './client';
import { User } from './userEndpoints';

export interface Contribution {
  id: string;
  user_id: string;
  amount_cents: number;
  status: 'pending' | 'confirmed' | 'failed' | 'rejected';
  confirmed_at?: string;
  note?: string;
  // S40: User-submitted contribution fields
  sent_at?: string;
  payment_method?: 'bank_transfer' | 'zelle' | 'cash_app' | 'venmo' | 'other';
  reference?: string;
  evidence_url?: string;
  rejection_reason?: string;
  submitted_by_user?: boolean;
  created_at: string;
  updated_at: string;
  user?: User;
  meets_minimum?: boolean;
  shortfall_cents?: number;
}

export interface CreateContributionRequest {
  user_id: string;
  amount_cents: number;
  note?: string;
}

export interface RejectContributionRequest {
  reason: string;
}

export interface ContributionListParams {
  status?: 'pending' | 'confirmed' | 'failed';
  user_id?: string;
}

interface ContributionListResponse {
  contributions: Contribution[];
}

interface ContributionResponse {
  contribution: Contribution;
}

// S40: User contribution status and submission
export interface PaymentMethod {
  value: string;
  label: string;
}

export interface ContributionStatus {
  required_cents: number;
  funded_cents: number;
  remaining_cents: number;
  pending_cents: number;
  status: 'not_funded' | 'partially_funded' | 'pending_review' | 'eligible';
  eligible: boolean;
  base_principal_cents: number;
  payment_instructions: string;
  payment_methods: PaymentMethod[];
  pending_contributions: Array<{
    id: string;
    amount_cents: number;
    sent_at: string;
    payment_method: string;
    reference?: string;
    created_at: string;
  }>;
  // S-C1: Pending deposit transfer
  pending_deposit?: {
    id: string;
    amount_cents: number;
    status: string;
    rail: string;
    memo: string;
    created_at: string;
    provider_transfer_id?: string;
    failure_reason?: string;
  } | null;
}

export interface SubmitContributionRequest {
  amount_cents: number;
  sent_at: string;
  payment_method: string;
  reference?: string;
  evidence_url?: string;
  note?: string;
}

export const contributionEndpoints = {
  // S40: User-facing contribution endpoints
  getStatus: async () => {
    return await apiClient.get<ContributionStatus>('/api/me/contributions/status');
  },

  submit: async (data: SubmitContributionRequest) => {
    return await apiClient.post<{
      contribution: Contribution;
      updated_status: {
        funded_cents: number;
        required_cents: number;
        remaining_cents: number;
        pending_cents: number;
        status: string;
      };
    }>('/api/me/contributions', data);
  },

  // S-C1: Create initial deposit via bank
  createInitialDeposit: async () => {
    return await apiClient.post<{
      transfer: {
        id: string;
        amount_cents: number;
        status: string;
        rail: string;
        memo: string;
        provider_transfer_id?: string;
        failure_reason?: string;
        created_at: string;
        completed_at?: string;
      };
      updated_status: {
        funded_cents: number;
        required_cents: number;
        remaining_cents: number;
        has_pending_deposit: boolean;
      };
    }>('/api/me/contributions/initial_deposit');
  },

  // S-C2: Refresh transfer status
  refreshTransferStatus: async (transferId: string) => {
    return await apiClient.post<{
      transfer: {
        id: string;
        status: string;
        provider_transfer_id?: string;
        failure_reason?: string;
        amount_cents: number;
        created_at: string;
        completed_at?: string;
      };
    }>(`/api/payments/transfers/${transferId}/refresh`);
  },

  admin: {
    // List contributions with optional filtering
    list: async (params?: ContributionListParams) => {
      const response = await apiClient.get<ContributionListResponse>('/api/admin/contributions', { params });
      return response.contributions; // Extract array from wrapper
    },

    // Get specific contribution
    get: async (contributionId: string) => {
      const response = await apiClient.get<ContributionResponse>(`/api/admin/contributions/${contributionId}`);
      return response.contribution; // Extract from wrapper
    },

    // Create new contribution
    create: async (data: CreateContributionRequest) => {
      const response = await apiClient.post<ContributionResponse>('/api/admin/contributions', data);
      return response.contribution; // Extract from wrapper
    },

    // Confirm contribution
    confirm: async (contributionId: string) => {
      const response = await apiClient.post<ContributionResponse>(`/api/admin/contributions/${contributionId}/confirm`);
      return response.contribution; // Extract from wrapper
    },

    // Reject contribution
    reject: async (contributionId: string, data: RejectContributionRequest) => {
      const response = await apiClient.post<ContributionResponse>(`/api/admin/contributions/${contributionId}/reject`, data);
      return response.contribution; // Extract from wrapper
    },
  },
};
